import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  StyleSheet,
  ScrollView,
  Text,
  TouchableOpacity,
  View as RNView,
  Modal,
  Pressable,
  ActivityIndicator,
  RefreshControl,
  useColorScheme,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { View } from "../../../components/shared/Themed";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import {
  getAllAppointments,
  getWarehouseList,
} from "../../../redux/dispatcher/portalDispatcher";

import Icon from "react-native-vector-icons/Feather";

interface Warehouse {
  _id: string;
  name: string;
  calendarId: string;
  timezone: string;
}

const FeatherIcon: React.ComponentType<any> = Icon as any;
// ─── Types ────────────────────────────────────────────────────────────────────
interface PortalAppointment {
  _id: string;
  calendarEventId: string;
  summary: string;
  description?: string;
  startDateTime: string;
  endDateTime: string;
  timeZone?: string;
  status: "SCHEDULED" | "CANCELLED" | "COMPLETED";
  notes?: string;
  organization?: string;
  createdBy?: { _id: string; auth?: { email?: string } };
  orderDetailId?: string;
  orderDetail?: {
    _id?: string;
    warehouse?: { _id?: string; calendarId?: string };
  };
}

interface CalendarEvent {
  id: string;
  summary: string;
  description?: string;
  startDateTime: string;
  endDateTime: string;
  status: string;
  notes?: string;
  calendarEventId: string;
  createdBy?: { email?: string };
  warehouseCalendarId?: string;
  raw: PortalAppointment;
}

type CalendarView = "month" | "week" | "day";

// ─── Constants ────────────────────────────────────────────────────────────────
const DAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const STATUS_COLOR: Record<string, string> = {
  SCHEDULED: "#61B8FF",
  COMPLETED: "#16A765",
  CANCELLED: "#F87171",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getEventColor = (e: CalendarEvent) =>
  STATUS_COLOR[e.status] ?? STATUS_COLOR.SCHEDULED;

const parseDate = (s: string): Date => new Date(s);

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const formatTime = (d: Date) =>
  d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const formatDateLabel = (d: Date) =>
  `${DAYS_SHORT[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;

const startOfWeek = (d: Date): Date => {
  const result = new Date(d);
  result.setDate(d.getDate() - d.getDay());
  result.setHours(0, 0, 0, 0);
  return result;
};

const startOfMonth = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), 1);

/** Map raw PortalAppointment → CalendarEvent used by the UI */
const toCalendarEvent = (a: PortalAppointment): CalendarEvent => ({
  id: a._id,
  summary: a.summary,
  description: a.description,
  startDateTime: a.startDateTime,
  endDateTime: a.endDateTime,
  status: a.status,
  notes: a.notes,
  calendarEventId: a.calendarEventId,
  createdBy: { email: a.createdBy?.auth?.email },
  warehouseCalendarId: a.orderDetail?.warehouse?.calendarId,
  raw: a,
});

// ─── Sub-components ───────────────────────────────────────────────────────────
const StatusBadge = ({ status }: { status: string }) => {
  const color = STATUS_COLOR[status] ?? "#9CA3AF";
  return (
    <RNView
      style={[
        apptStyles.statusBadge,
        { backgroundColor: color + "22", borderColor: color },
      ]}
    >
      <RNView style={[apptStyles.statusDot, { backgroundColor: color }]} />
      <Text style={[apptStyles.statusText, { color }]}>{status}</Text>
    </RNView>
  );
};

const EventDetailModal = ({
  event,
  visible,
  onClose,
}: {
  event: CalendarEvent | null;
  visible: boolean;
  onClose: () => void;
}) => {
  if (!event) return null;
  const start = parseDate(event.startDateTime);
  const end = parseDate(event.endDateTime);
  const color = getEventColor(event);

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <Pressable style={apptStyles.modalOverlay} onPress={onClose}>
        <Pressable
          style={apptStyles.eventDetailCard}
          onPress={(e) => e.stopPropagation()}
        >
          <RNView
            style={[apptStyles.eventDetailColorBar, { backgroundColor: color }]}
          />
          <ScrollView
            style={{ padding: 20 }}
            showsVerticalScrollIndicator={false}
          >
            <RNView style={{ marginBottom: 12 }}>
              <Text style={apptStyles.eventDetailTitle}>{event.summary}</Text>
              <StatusBadge status={event.status} />
            </RNView>
            <RNView style={apptStyles.eventDetailRow}>
              <FeatherIcon name="clock" size={14} color="#6B7280" />
              <Text style={apptStyles.eventDetailMeta}>
                {formatDateLabel(start)}
                {"\n"}
                {formatTime(start)} – {formatTime(end)}
              </Text>
            </RNView>
            {!!event.description && (
              <RNView style={apptStyles.eventDetailRow}>
                <FeatherIcon name="file-text" size={14} color="#6B7280" />
                <Text style={apptStyles.eventDetailMeta}>
                  {event.description}
                </Text>
              </RNView>
            )}
            {!!event.notes && (
              <RNView style={apptStyles.eventDetailRow}>
                <FeatherIcon name="edit-3" size={14} color="#6B7280" />
                <Text style={apptStyles.eventDetailMeta}>{event.notes}</Text>
              </RNView>
            )}
            {!!event.createdBy?.email && (
              <RNView style={apptStyles.eventDetailRow}>
                <FeatherIcon name="user" size={14} color="#6B7280" />
                <Text style={apptStyles.eventDetailMeta}>
                  {event.createdBy.email}
                </Text>
              </RNView>
            )}
            {!!event.calendarEventId && (
              <>
                <RNView style={apptStyles.divider} />
                <Text style={apptStyles.sectionLabel}>
                  GOOGLE CALENDAR EVENT ID
                </Text>
                <Text style={[apptStyles.eventDetailMeta, { fontSize: 11 }]}>
                  {event.calendarEventId}
                </Text>
              </>
            )}
          </ScrollView>
          <TouchableOpacity style={apptStyles.closeBtn} onPress={onClose}>
            <FeatherIcon name="x" size={18} color="#6B7280" />
          </TouchableOpacity>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const EventPill = ({
  event,
  compact = false,
  onPress,
}: {
  event: CalendarEvent;
  compact?: boolean;
  onPress: (e: CalendarEvent) => void;
}) => {
  const color = getEventColor(event);
  const start = parseDate(event.startDateTime);
  return (
    <TouchableOpacity
      style={[
        apptStyles.eventPill,
        { backgroundColor: color + "22", borderLeftColor: color },
        compact && apptStyles.eventPillCompact,
      ]}
      onPress={() => onPress(event)}
      activeOpacity={0.75}
    >
      {!compact && (
        <Text style={[apptStyles.eventPillTime, { color }]}>
          {formatTime(start)}
        </Text>
      )}
      <Text
        style={[
          apptStyles.eventPillTitle,
          { color },
          compact && { fontSize: 10 },
        ]}
        numberOfLines={compact ? 1 : 2}
      >
        {event.summary}
      </Text>
    </TouchableOpacity>
  );
};

const MonthView = ({
  currentDate,
  events,
  onDayPress,
  onEventPress,
  selectedDate,
}: {
  currentDate: Date;
  events: CalendarEvent[];
  onDayPress: (d: Date) => void;
  onEventPress: (e: CalendarEvent) => void;
  selectedDate: Date;
}) => {
  const today = new Date();
  const startDay = startOfWeek(startOfMonth(currentDate));
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(startDay);
    d.setDate(startDay.getDate() + i);
    return d;
  });

  const eventsOnDay = (d: Date) =>
    events.filter((e) => isSameDay(parseDate(e.startDateTime), d));

  return (
    <RNView>
      <RNView style={apptStyles.weekHeaderRow}>
        {DAYS_SHORT.map((d) => (
          <RNView key={d} style={apptStyles.weekHeaderCell}>
            <Text style={apptStyles.weekHeaderText}>{d}</Text>
          </RNView>
        ))}
      </RNView>
      <RNView style={apptStyles.monthGrid}>
        {cells.map((cell, idx) => {
          const inMonth = cell.getMonth() === currentDate.getMonth();
          const isToday = isSameDay(cell, today);
          const isSel = isSameDay(cell, selectedDate);
          const dayEvts = eventsOnDay(cell);
          return (
            <TouchableOpacity
              key={idx}
              style={[
                apptStyles.monthCell,
                isSel && apptStyles.monthCellSelected,
              ]}
              onPress={() => onDayPress(cell)}
              activeOpacity={0.7}
            >
              <RNView
                style={[
                  apptStyles.dayNumberWrap,
                  isToday && apptStyles.todayCircle,
                ]}
              >
                <Text
                  style={[
                    apptStyles.dayNumber,
                    !inMonth && apptStyles.dayNumberFaded,
                    isToday && apptStyles.dayNumberToday,
                  ]}
                >
                  {cell.getDate()}
                </Text>
              </RNView>
              {dayEvts.slice(0, 2).map((ev, i) => (
                <EventPill key={i} event={ev} compact onPress={onEventPress} />
              ))}
              {dayEvts.length > 2 && (
                <Text style={apptStyles.moreEvents}>
                  +{dayEvts.length - 2} more
                </Text>
              )}
            </TouchableOpacity>
          );
        })}
      </RNView>
    </RNView>
  );
};

const WeekView = ({
  currentDate,
  events,
  onEventPress,
}: {
  currentDate: Date;
  events: CalendarEvent[];
  onEventPress: (e: CalendarEvent) => void;
}) => {
  const today = new Date();
  const weekStart = startOfWeek(currentDate);
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    return d;
  });

  return (
    <ScrollView horizontal showsHorizontalScrollIndicator={false}>
      <RNView style={{ flexDirection: "row" }}>
        {days.map((day, idx) => {
          const isToday = isSameDay(day, today);
          const dayEvts = events.filter((e) =>
            isSameDay(parseDate(e.startDateTime), day),
          );
          return (
            <RNView key={idx} style={apptStyles.weekDayColumn}>
              <RNView
                style={[
                  apptStyles.weekDayHeader,
                  isToday && apptStyles.weekDayHeaderToday,
                ]}
              >
                <Text
                  style={[
                    apptStyles.weekDayName,
                    isToday && apptStyles.weekDayNameToday,
                  ]}
                >
                  {DAYS_SHORT[day.getDay()]}
                </Text>
                <Text
                  style={[
                    apptStyles.weekDayDate,
                    isToday && apptStyles.weekDayDateToday,
                  ]}
                >
                  {day.getDate()}
                </Text>
              </RNView>
              <ScrollView
                style={apptStyles.weekDayEvents}
                showsVerticalScrollIndicator={false}
              >
                {dayEvts.length === 0 ? (
                  <Text style={apptStyles.noEventsText}>—</Text>
                ) : (
                  dayEvts.map((ev, i) => (
                    <EventPill key={i} event={ev} onPress={onEventPress} />
                  ))
                )}
              </ScrollView>
            </RNView>
          );
        })}
      </RNView>
    </ScrollView>
  );
};

const DayView = ({
  currentDate,
  events,
  onEventPress,
}: {
  currentDate: Date;
  events: CalendarEvent[];
  onEventPress: (e: CalendarEvent) => void;
}) => {
  const dayEvts = events.filter((e) =>
    isSameDay(parseDate(e.startDateTime), currentDate),
  );

  return (
    <ScrollView style={{ padding: 12 }} showsVerticalScrollIndicator={false}>
      {dayEvts.length === 0 ? (
        <RNView style={apptStyles.emptyDay}>
          <FeatherIcon name="calendar" size={36} color="#D1D5DB" />
          <Text style={apptStyles.emptyDayText}>No appointments this day</Text>
        </RNView>
      ) : (
        dayEvts.map((ev, i) => {
          const start = parseDate(ev.startDateTime);
          const end = parseDate(ev.endDateTime);
          const color = getEventColor(ev);
          return (
            <TouchableOpacity
              key={i}
              style={[apptStyles.dayEventCard, { borderLeftColor: color }]}
              onPress={() => onEventPress(ev)}
              activeOpacity={0.75}
            >
              <RNView style={{ flex: 1 }}>
                <RNView
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: 4,
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <Text style={[apptStyles.dayEventTitle, { color }]}>
                    {ev.summary}
                  </Text>
                  <StatusBadge status={ev.status} />
                </RNView>
                <Text style={apptStyles.dayEventTime}>
                  {formatTime(start)} – {formatTime(end)}
                </Text>
                {!!ev.description && (
                  <RNView
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      marginTop: 4,
                      gap: 4,
                    }}
                  >
                    <FeatherIcon name="file-text" size={11} color="#9CA3AF" />
                    <Text style={apptStyles.dayEventMeta} numberOfLines={1}>
                      {ev.description}
                    </Text>
                  </RNView>
                )}
                {!!ev.notes && (
                  <RNView
                    style={{
                      flexDirection: "row",
                      alignItems: "center",
                      marginTop: 3,
                      gap: 4,
                    }}
                  >
                    <FeatherIcon name="edit-3" size={11} color="#9CA3AF" />
                    <Text style={apptStyles.dayEventMeta} numberOfLines={1}>
                      {ev.notes}
                    </Text>
                  </RNView>
                )}
              </RNView>
              <FeatherIcon name="chevron-right" size={16} color="#D1D5DB" />
            </TouchableOpacity>
          );
        })
      )}
    </ScrollView>
  );
};

// ─── Main Screen ──────────────────────────────────────────────────────────────

const DeliveryAppointmentsScreen = ({ route, navigation }: any) => {
  const dispatch = useDispatch<any>(); //
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);

  const { userInfo, userRoles } = useSelector(
    (state: any) => state.configurations,
  );

  const canAccessAllAppointments =
    userRoles.some((role: string) => ["ADMIN", "OWNER"].includes(role)) ||
    userInfo?.team === "operation";

  const timezoneId = canAccessAllAppointments ? null : userInfo?.zone_info;

  // ── Redux state ───────────────────────────────────────────────────────────
  // appointments & loading come from portalSlice.appointments
  const { loading, appointments: rawAppointments } = useSelector(
    (state: any) => state.portal.appointments,
  );

  const { loading: warehouseLoading, warehouses } = useSelector(
    (state: any) => state.portal.warehouses,
  );
  console.log(warehouses, "warehouses ");

  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("ALL");
  const [warehousePickerVisible, setWarehousePickerVisible] =
    useState<boolean>(false);

  useEffect(() => {
    if (canAccessAllAppointments) {
      dispatch(getWarehouseList());
    }
  }, [canAccessAllAppointments]);

  const selectedWarehouse = useMemo(
    () =>
      warehouses?.find((w: Warehouse) => w._id === selectedWarehouseId) ?? null,
    [warehouses, selectedWarehouseId],
  );

  const effectiveCalendarId = canAccessAllAppointments
    ? selectedWarehouseId === "ALL"
      ? null
      : (selectedWarehouse?.calendarId ?? null)
    : timezoneId;

  // ── Local UI state ────────────────────────────────────────────────────────
  const [view, setView] = useState<CalendarView>("month");
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );
  const [eventModalVisible, setEventModalVisible] = useState<boolean>(false);

  // ── Map raw redux appointments → CalendarEvent[] ─────────────────────────
  // Memoised so it only recalculates when rawAppointments changes
  const allEvents: CalendarEvent[] = useMemo(
    () => (rawAppointments ?? []).map(toCalendarEvent),
    [rawAppointments],
  );

  // ─── Fetch from backend ───────────────────────────────────────────────────
  const getDateWindow = (v: CalendarView, d: Date) => {
    const toDateString = (date: Date) =>
      `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    let startDate: Date;
    let endDate: Date;

    if (v === "month") {
      startDate = new Date(d.getFullYear(), d.getMonth(), 1);
      endDate = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    } else if (v === "week") {
      startDate = startOfWeek(d);
      endDate = new Date(startDate);
      endDate.setDate(startDate.getDate() + 6);
    } else {
      startDate = new Date(d);
      endDate = new Date(d);
    }

    return {
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
    };
  };

  const events: CalendarEvent[] = useMemo(() => {
    return allEvents.filter((e) => {
      const matchesCalendar =
        !timezoneId || e.warehouseCalendarId === timezoneId;
      return matchesCalendar;
    });
  }, [allEvents, timezoneId]);

  const load = async (
    showRefresh = false,
    v: CalendarView = view,
    d: Date = currentDate,
  ) => {
    if (showRefresh) setRefreshing(true);
    try {
      const { startDate, endDate } = getDateWindow(v, d);
      await dispatch(
        getAllAppointments({
          startDate,
          endDate,
          // non-admin: auto-filter by their own zone_info, unchanged
          ...(!canAccessAllAppointments && {
            timezone: timezoneId ?? undefined,
          }),
          // admin/owner: explicit warehouse filter via dropdown
          ...(canAccessAllAppointments &&
            selectedWarehouseId !== "ALL" && {
              calendarId: effectiveCalendarId ?? undefined,
            }),
        }),
      ).unwrap();
    } catch (err) {
      console.error("Failed to load appointments:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // Re-fetch whenever view or currentDate changes
  useEffect(() => {
    load(false, view, currentDate);
  }, [view, currentDate, effectiveCalendarId]);

  // ── Calendar navigation ───────────────────────────────────────────────────
  const navigate = (direction: -1 | 1) => {
    const next = new Date(currentDate);
    if (view === "month") next.setMonth(next.getMonth() + direction);
    else if (view === "week") next.setDate(next.getDate() + direction * 7);
    else next.setDate(next.getDate() + direction);
    setCurrentDate(next);
  };

  const goToToday = () => {
    const now = new Date();
    setCurrentDate(now);
    setSelectedDate(now);
  };

  const getHeaderLabel = (): string => {
    if (view === "month")
      return `${MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (view === "week") {
      const ws = startOfWeek(currentDate);
      const we = new Date(ws);
      we.setDate(ws.getDate() + 6);
      return `${MONTHS[ws.getMonth()]} ${ws.getDate()} – ${we.getDate()}, ${ws.getFullYear()}`;
    }
    return formatDateLabel(currentDate);
  };

  const handleDayPress = (d: Date) => {
    setSelectedDate(d);
    setCurrentDate(d);
    setView("day");
    load(false, "day", d);
  };

  const handleEventPress = (e: CalendarEvent) => {
    setSelectedEvent(e);
    setEventModalVisible(true);
  };

  return (
    <View style={{ flex: 1, backgroundColor: "#f2f2f2" }}>
      {/* ── Top bar ───────────────────────────────────────────────────────── */}
      <RNView style={apptStyles.headerBar}>
        <RNView style={apptStyles.headerLeft}>
          <TouchableOpacity
            onPress={() => navigate(-1)}
            style={apptStyles.navBtn}
          >
            <FeatherIcon name="chevron-left" size={18} color="#374151" />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => navigate(1)}
            style={apptStyles.navBtn}
          >
            <FeatherIcon name="chevron-right" size={18} color="#374151" />
          </TouchableOpacity>
          <Text style={apptStyles.headerLabel}>{getHeaderLabel()}</Text>
        </RNView>

        <RNView style={apptStyles.headerRight}>
          <TouchableOpacity style={apptStyles.todayBtn} onPress={goToToday}>
            <Text style={apptStyles.todayBtnText}>Today</Text>
          </TouchableOpacity>
          <RNView style={apptStyles.viewSwitcher}>
            {(["month", "week", "day"] as CalendarView[]).map((v) => (
              <TouchableOpacity
                key={v}
                style={[
                  apptStyles.viewTab,
                  view === v && apptStyles.viewTabActive,
                ]}
                onPress={() => setView(v)}
              >
                <Text
                  style={[
                    apptStyles.viewTabText,
                    view === v && apptStyles.viewTabTextActive,
                  ]}
                >
                  {v.charAt(0).toUpperCase() + v.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </RNView>
        </RNView>
      </RNView>

      {canAccessAllAppointments && (
        <RNView style={apptStyles.warehouseFilterWrap}>
          <TouchableOpacity
            style={apptStyles.warehouseBtn}
            onPress={() => setWarehousePickerVisible((prev) => !prev)}
          >
            <FeatherIcon name="map-pin" size={13} color="#374151" />
            <Text style={apptStyles.warehouseBtnText} numberOfLines={1}>
              {selectedWarehouseId === "ALL"
                ? "All Warehouses"
                : (selectedWarehouse?.name ?? "Select")}
            </Text>
            <FeatherIcon
              name={warehousePickerVisible ? "chevron-up" : "chevron-down"}
              size={13}
              color="#9CA3AF"
            />
          </TouchableOpacity>

          {warehousePickerVisible && (
            <RNView style={apptStyles.warehouseDropdown}>
              <TouchableOpacity
                style={[
                  apptStyles.warehouseOption,
                  selectedWarehouseId === "ALL" &&
                    apptStyles.warehouseOptionActive,
                ]}
                onPress={() => {
                  setSelectedWarehouseId("ALL");
                  setWarehousePickerVisible(false);
                }}
              >
                <Text
                  style={[
                    apptStyles.warehouseOptionText,
                    selectedWarehouseId === "ALL" &&
                      apptStyles.warehouseOptionTextActive,
                  ]}
                >
                  All Warehouses
                </Text>
                {selectedWarehouseId === "ALL" && (
                  <FeatherIcon name="check" size={16} color="#61B8FF" />
                )}
              </TouchableOpacity>

              {warehouseLoading ? (
                <ActivityIndicator
                  size="small"
                  color="#61B8FF"
                  style={{ marginVertical: 12 }}
                />
              ) : (
                (warehouses ?? []).map((w: Warehouse) => (
                  <TouchableOpacity
                    key={w._id}
                    style={[
                      apptStyles.warehouseOption,
                      selectedWarehouseId === w._id &&
                        apptStyles.warehouseOptionActive,
                    ]}
                    onPress={() => {
                      setSelectedWarehouseId(w._id);
                      setWarehousePickerVisible(false);
                    }}
                  >
                    <Text
                      style={[
                        apptStyles.warehouseOptionText,
                        selectedWarehouseId === w._id &&
                          apptStyles.warehouseOptionTextActive,
                      ]}
                    >
                      {w.name}
                    </Text>
                    {selectedWarehouseId === w._id && (
                      <FeatherIcon name="check" size={16} color="#61B8FF" />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </RNView>
          )}
        </RNView>
      )}

      <Modal
        visible={warehousePickerVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setWarehousePickerVisible(false)}
      >
        <Pressable
          style={apptStyles.modalOverlay}
          onPress={() => setWarehousePickerVisible(false)}
        >
          <Pressable
            style={apptStyles.warehousePickerCard}
            onPress={(e) => e.stopPropagation()}
          >
            <Text style={apptStyles.sectionLabel}>FILTER BY WAREHOUSE</Text>
            <ScrollView showsVerticalScrollIndicator={false}>
              <TouchableOpacity
                style={[
                  apptStyles.warehouseOption,
                  selectedWarehouseId === "ALL" &&
                    apptStyles.warehouseOptionActive,
                ]}
                onPress={() => {
                  setSelectedWarehouseId("ALL");
                  setWarehousePickerVisible(false);
                }}
              >
                <Text
                  style={[
                    apptStyles.warehouseOptionText,
                    selectedWarehouseId === "ALL" &&
                      apptStyles.warehouseOptionTextActive,
                  ]}
                >
                  All Warehouses
                </Text>
                {selectedWarehouseId === "ALL" && (
                  <FeatherIcon name="check" size={16} color="#61B8FF" />
                )}
              </TouchableOpacity>
              {warehouseLoading ? (
                <ActivityIndicator
                  size="small"
                  color="#61B8FF"
                  style={{ marginTop: 12 }}
                />
              ) : (
                (warehouses ?? []).map((w: Warehouse) => (
                  <TouchableOpacity
                    key={w._id}
                    style={[
                      apptStyles.warehouseOption,
                      selectedWarehouseId === w._id &&
                        apptStyles.warehouseOptionActive,
                    ]}
                    onPress={() => {
                      setSelectedWarehouseId(w._id);
                      setWarehousePickerVisible(false);
                    }}
                  >
                    <Text
                      style={[
                        apptStyles.warehouseOptionText,
                        selectedWarehouseId === w._id &&
                          apptStyles.warehouseOptionTextActive,
                      ]}
                    >
                      {w.name}
                    </Text>
                    {selectedWarehouseId === w._id && (
                      <FeatherIcon name="check" size={16} color="#61B8FF" />
                    )}
                  </TouchableOpacity>
                ))
              )}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* ── Calendar ──────────────────────────────────────────────────────── */}
      <RNView style={apptStyles.body}>
        {loading && !refreshing ? (
          <RNView style={apptStyles.centered}>
            <ActivityIndicator size="large" color="#61B8FF" />
            <Text style={apptStyles.loadingText}>Loading appointments…</Text>
          </RNView>
        ) : (
          <ScrollView
            style={{ flex: 1 }}
            showsVerticalScrollIndicator={false}
            scrollEnabled={view !== "week"}
            refreshControl={
              <RefreshControl
                refreshing={refreshing}
                onRefresh={() => load(true)}
                colors={["#61B8FF"]}
                tintColor="#61B8FF"
              />
            }
          >
            {view === "month" && (
              <MonthView
                currentDate={currentDate}
                events={events}
                onDayPress={handleDayPress}
                onEventPress={handleEventPress}
                selectedDate={selectedDate}
              />
            )}
            {view === "week" && (
              <WeekView
                currentDate={currentDate}
                events={events}
                onEventPress={handleEventPress}
              />
            )}
            {view === "day" && (
              <DayView
                currentDate={currentDate}
                events={events}
                onEventPress={handleEventPress}
              />
            )}
          </ScrollView>
        )}
      </RNView>

      <EventDetailModal
        event={selectedEvent}
        visible={eventModalVisible}
        onClose={() => setEventModalVisible(false)}
      />
    </View>
  );
};

export default DeliveryAppointmentsScreen;

// ─── Styles ───────────────────────────────────────────────────────────────────

const apptStyles = StyleSheet.create({
  headerBar: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
    flexWrap: "wrap",
    gap: 8,
  },
  headerLeft: { flexDirection: "row", alignItems: "center", gap: 4 },
  headerRight: { flexDirection: "row", alignItems: "center", gap: 8 },
  navBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "#F3F4F6",
    justifyContent: "center",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  headerLabel: {
    fontSize: 15,
    fontWeight: "700",
    color: "#111827",
    marginLeft: 6,
  },
  todayBtn: {
    paddingVertical: 6,
    paddingHorizontal: 14,
    borderRadius: 999,
    backgroundColor: "#F1F5F9",
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  todayBtnText: { fontSize: 13, fontWeight: "600", color: "#374151" },
  viewSwitcher: {
    flexDirection: "row",
    backgroundColor: "#F1F5F9",
    borderRadius: 8,
    padding: 3,
    borderWidth: 1,
    borderColor: "#E2E8F0",
  },
  viewTab: { paddingVertical: 5, paddingHorizontal: 12, borderRadius: 6 },
  viewTabActive: { backgroundColor: "#61B8FF" },
  viewTabText: { fontSize: 12, fontWeight: "600", color: "#6B7280" },
  viewTabTextActive: { color: "#fff" },

  filterBar: {
    backgroundColor: "#fff",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },
  filterChip: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 5,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    gap: 6,
  },
  filterChipDot: { width: 7, height: 7, borderRadius: 4 },
  filterChipText: { fontSize: 12, fontWeight: "600", color: "#475569" },
  filterChipTextActive: { color: "#fff" },
  filterChipCount: {
    backgroundColor: "#E2E8F0",
    borderRadius: 999,
    paddingHorizontal: 6,
    paddingVertical: 1,
  },
  filterChipCountText: { fontSize: 10, fontWeight: "700", color: "#6B7280" },

  body: { flex: 1, backgroundColor: "#fff" },
  centered: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
    gap: 12,
  },
  loadingText: { fontSize: 14, color: "#9CA3AF", marginTop: 8 },

  weekHeaderRow: {
    flexDirection: "row",
    backgroundColor: "#F8FAFC",
    borderBottomWidth: 1,
    borderBottomColor: "#E2E8F0",
  },
  weekHeaderCell: { flex: 1, alignItems: "center", paddingVertical: 8 },
  weekHeaderText: {
    fontSize: 11,
    fontWeight: "700",
    color: "#9CA3AF",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  monthGrid: { flexDirection: "row", flexWrap: "wrap" },
  monthCell: {
    width: `${100 / 7}%` as any,
    minHeight: 90,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#E2E8F0",
    padding: 4,
    backgroundColor: "#fff",
  },
  monthCellSelected: { backgroundColor: "#EFF6FF" },
  dayNumberWrap: {
    width: 26,
    height: 26,
    borderRadius: 13,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 2,
  },
  todayCircle: { backgroundColor: "#61B8FF" },
  dayNumber: { fontSize: 12, fontWeight: "600", color: "#374151" },
  dayNumberFaded: { color: "#D1D5DB" },
  dayNumberToday: { color: "#fff" },
  moreEvents: {
    fontSize: 9,
    color: "#9CA3AF",
    fontWeight: "600",
    marginTop: 1,
    paddingLeft: 2,
  },

  weekDayColumn: {
    width: 130,
    borderRightWidth: 1,
    borderColor: "#E2E8F0",
    minHeight: 400,
  },
  weekDayHeader: {
    alignItems: "center",
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderColor: "#E2E8F0",
    backgroundColor: "#F8FAFC",
  },
  weekDayHeaderToday: { backgroundColor: "#EFF6FF" },
  weekDayName: {
    fontSize: 11,
    fontWeight: "700",
    color: "#9CA3AF",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  weekDayNameToday: { color: "#61B8FF" },
  weekDayDate: {
    fontSize: 20,
    fontWeight: "700",
    color: "#111827",
    marginTop: 2,
  },
  weekDayDateToday: { color: "#61B8FF" },
  weekDayEvents: { padding: 6, maxHeight: 500 },
  noEventsText: {
    textAlign: "center",
    color: "#D1D5DB",
    fontSize: 20,
    marginTop: 24,
  },

  dayEventCard: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F8FAFC",
    borderRadius: 10,
    borderLeftWidth: 4,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.04,
    shadowRadius: 4,
    elevation: 1,
  },
  dayEventTitle: { fontSize: 14, fontWeight: "700", marginBottom: 3 },
  dayEventTime: { fontSize: 12, color: "#6B7280", fontWeight: "500" },
  dayEventMeta: { fontSize: 11, color: "#9CA3AF", flex: 1 },
  emptyDay: { alignItems: "center", paddingTop: 60, gap: 12 },
  emptyDayText: { fontSize: 14, color: "#D1D5DB", fontWeight: "500" },

  eventPill: {
    borderLeftWidth: 3,
    borderRadius: 4,
    paddingVertical: 3,
    paddingHorizontal: 5,
    marginBottom: 3,
  },
  eventPillCompact: { paddingVertical: 2, paddingHorizontal: 4 },
  eventPillTime: { fontSize: 10, fontWeight: "700", marginBottom: 1 },
  eventPillTitle: { fontSize: 11, fontWeight: "600" },

  statusBadge: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 999,
    borderWidth: 1,
    gap: 5,
    marginTop: 4,
  },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  statusText: {
    fontSize: 10,
    fontWeight: "700",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },

  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.3)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  eventDetailCard: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 16,
    maxHeight: "85%",
    overflow: "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 16 },
    shadowOpacity: 0.15,
    shadowRadius: 32,
    elevation: 16,
  },
  eventDetailColorBar: { height: 6, width: "100%" },
  eventDetailTitle: {
    fontSize: 18,
    fontWeight: "800",
    color: "#111827",
    lineHeight: 24,
  },
  eventDetailRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginBottom: 10,
    gap: 8,
  },
  eventDetailMeta: { fontSize: 13, color: "#4B5563", flex: 1, lineHeight: 18 },
  divider: { height: 1, backgroundColor: "#F1F5F9", marginVertical: 12 },
  sectionLabel: {
    fontSize: 10,
    fontWeight: "700",
    color: "#9CA3AF",
    letterSpacing: 1,
    marginBottom: 6,
  },
  closeBtn: {
    position: "absolute",
    top: 14,
    right: 14,
    width: 30,
    height: 30,
    borderRadius: 15,
    backgroundColor: "#F1F5F9",
    justifyContent: "center",
    alignItems: "center",
  },

  warehouseBtn: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: "#F1F5F9",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    gap: 6,
    maxWidth: 160,
  },
  warehouseBtnText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#374151",
    flexShrink: 1,
  },
  warehousePickerCard: {
    width: "100%",
    maxWidth: 360,
    maxHeight: "70%",
    backgroundColor: "#fff",
    borderRadius: 16,
    padding: 16,
  },
  warehouseOption: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 10,
    borderRadius: 8,
  },
  warehouseOptionActive: { backgroundColor: "#EFF6FF" },
  warehouseOptionText: { fontSize: 14, fontWeight: "600", color: "#374151" },
  warehouseOptionTextActive: { color: "#61B8FF" },

  warehouseFilterWrap: {
    position: "relative",
  },
  warehouseDropdown: {
    position: "absolute",
    top: "100%",
    right: 0,
    marginTop: 6,
    width: 220,
    backgroundColor: "#fff",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    paddingVertical: 6,
    paddingHorizontal: 6,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    zIndex: 50,
    maxHeight: 280,
  },
});
